import { Clock, User, Calendar, Flag, Tag, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface Task {
  id: string;
  title: string;
  project: string;
  projectId?: string;
  status: 'Backlog' | 'Not Started' | 'In Progress' | 'Quality Check' | 'Done';
  timeLogged: string;
  dueDate?: string;
  priority: 'high' | 'medium' | 'low';
  description?: string;
  assignee?: {
    name: string;
    avatar?: string;
  };
  tags?: string[];
  comments?: number;
}

interface TaskDetailModalProps {
  task: Task | null;
  isOpen: boolean;
  onClose: () => void;
  onStartTimer?: (task: Task) => void;
}

export function TaskDetailModal({ task, isOpen, onClose, onStartTimer }: TaskDetailModalProps) {
  if (!task) return null;

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'Done':
        return 'bg-[#27AE60] text-white';
      case 'In Progress':
        return 'bg-[#4A90E2] text-white';
      case 'Quality Check':
        return 'bg-[#F5A623] text-white';
      case 'Not Started':
        return 'bg-[#808080] text-white';
      case 'Backlog':
        return 'bg-[#b3b3b3] text-white';
      default:
        return 'bg-[#f6f6f6] text-[#808080]';
    }
  };

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'high':
        return 'text-[#e30840]';
      case 'medium':
        return 'text-[#F5A623]';
      case 'low':
        return 'text-[#27AE60]';
      default:
        return 'text-[#808080]';
    }
  };

  const getPriorityLabel = (priority: Task['priority']) => {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-[16px] font-semibold text-[#262626] pr-8">
            {task.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Status & Project Row */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`text-[11px] px-3 py-1 rounded-full ${getStatusColor(task.status)}`}>
              {task.status}
            </span>
            <span className="text-[12px] text-[#e30840] font-medium">{task.project}</span>
          </div>

          {/* Description */}
          {task.description && (
            <div className="p-3 bg-[#fafafa] rounded-md">
              <p className="text-[12px] text-[#808080] leading-relaxed">{task.description}</p>
            </div>
          )}

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4">
            {/* Time Logged */}
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#808080]" />
              <div>
                <p className="text-[10px] text-[#b3b3b3] uppercase">Time Logged</p>
                <p className="text-[13px] font-medium text-[#262626]">{task.timeLogged}</p>
              </div>
            </div>

            {/* Due Date */}
            {task.dueDate && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#808080]" />
                <div>
                  <p className="text-[10px] text-[#b3b3b3] uppercase">Due Date</p>
                  <p className="text-[13px] font-medium text-[#262626]">
                    {new Date(task.dueDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}

            {/* Priority */}
            <div className="flex items-center gap-2">
              <Flag className={`w-4 h-4 ${getPriorityColor(task.priority)}`} />
              <div>
                <p className="text-[10px] text-[#b3b3b3] uppercase">Priority</p>
                <p className={`text-[13px] font-medium ${getPriorityColor(task.priority)}`}>
                  {getPriorityLabel(task.priority)}
                </p>
              </div>
            </div>

            {/* Assignee */}
            {task.assignee && (
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-[#808080]" />
                <div>
                  <p className="text-[10px] text-[#b3b3b3] uppercase">Assignee</p>
                  <p className="text-[13px] font-medium text-[#262626]">{task.assignee.name}</p>
                </div>
              </div>
            )}
          </div>

          {/* Tags */}
          {task.tags && task.tags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-[#808080]" />
              {task.tags.map((tag, index) => (
                <span
                  key={index}
                  className="text-[10px] px-2 py-1 rounded-full bg-[#f5f5f5] text-[#808080]"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Comments */}
          {task.comments !== undefined && task.comments > 0 && (
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[#808080]" />
              <span className="text-[12px] text-[#808080]">{task.comments} comments</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-[#f5f5f5]">
          <Button variant="outline" onClick={onClose} className="text-[12px]">
            Close
          </Button>
          {task.status !== 'Done' && onStartTimer && (
            <Button 
              onClick={() => onStartTimer(task)} 
              className="text-[12px] bg-[#e30840] hover:bg-[#c50735]"
            >
              <Clock className="w-3 h-3 mr-2" />
              Start Timer
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
